import styled from "@emotion/styled";

import {
  Box,
  Typography as MuiTypography,
  ListItem,
  ListItemText,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { spacing, SpacingProps } from "@mui/system";

import { useTranslation } from "react-i18next";

import AzureADAvatar from "../../avatars/AzureADAvatar";
import DateTimeTranslate from "../../DateTimeTranslate";
import { AlarmOff, AlarmOn } from "@mui/icons-material";
import useAuth from "../../../hooks/useAuth";
import { IContract } from "../../../types/contract";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { getReminderDateForContract } from "../../../utils/string";

interface TypographyProps extends SpacingProps {
  component?: string;
}
const Typography = styled(MuiTypography)<TypographyProps>(spacing);

const Title = styled(Typography)`
  color: ${(props) => props.theme.palette.text.secondary};
  font-size: ${(props) => props.theme.typography.caption.fontSize};
  padding: ${(props) => props.theme.spacing(4)}
    ${(props) => props.theme.spacing(7)} ${(props) => props.theme.spacing(1)};
  opacity: 0.8;
  text-transform: uppercase;
  display: block;
`;

interface IContractExpiryNoteAsListItemProps {
  contract: IContract;
}

const ContractExpiryNoteAsListItem = (
  props: IContractExpiryNoteAsListItemProps
) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const { contract } = props;

  const users = useSelector((state: RootState) => state.users.users);

  const formattedBody = `${contract?.name} ${t("expires")}`;

  // check if taggedusers is an array or string
  // if it's a string, convert it to an array
  const taggedUsers = [
    ...(contract.ownerIds || []),
    ...(contract.adminIds || []),
    ...(contract.managerIds || []),
  ];

  const displayNames: string[] = [];

  if (taggedUsers.some((userId: string) => userId === user?.id)) {
    displayNames.push(t("You"));
  }

  taggedUsers
    ?.filter((userId: string) => userId !== user?.id)
    .map((userId: string) => {
      const taggedUser = users?.find((user) => user.id === userId);

      const displayName =
        taggedUser?.givenName ||
        taggedUser?.displayName ||
        taggedUser?.mail ||
        "";

      displayNames.push(displayName);
    });

  const label =
    displayNames?.length === 1 && taggedUsers[0] === user?.id
      ? "me"
      : displayNames?.length === 1
      ? "single"
      : "multi";

  const displayNamesString =
    displayNames?.length === 2
      ? displayNames?.join(` ${t("and")} `)
      : displayNames?.join(", ");

  const reminderDate = getReminderDateForContract(contract);
  const isPast = reminderDate && new Date(reminderDate) < new Date();

  return (
    <ListItem
      // component={props.onClick ? ListItemButton : ListItem}
      alignItems="flex-start"
      disablePadding
    >
      {/* <ListItemIcon>
        <AzureADAvatar userId={authorId} pictureOnly />
        {note.reminderDate ? <Alarm /> : <NoteAlt />}
      </ListItemIcon> */}

      <ListItemText>
        <Grid container flexDirection="column" spacing={1}>
          <Grid>
            {contract.dates?.endDate && (
              <Typography variant="subtitle2">
                <DateTimeTranslate
                  date={new Date(contract.dates.endDate)}
                  format="dateOnly"
                ></DateTimeTranslate>
              </Typography>
            )}
            <Typography variant="body1">{formattedBody}</Typography>
          </Grid>

          <Grid>
            <Box pt={1}>
              {taggedUsers?.map((userId: string) => {
                // return <Chip key={userId} size="small" mr={1} mb={1} label={userId} color="secondary" />;
                return (
                  <AzureADAvatar
                    key={`user-${userId}`}
                    userId={userId}
                    tagged
                  />
                );
                // return <Chip key={userId} size="small" mr={1} mb={1} label={`@${taggedUser?.displayName}`} color="default" />;
              })}
            </Box>
          </Grid>

          {reminderDate && displayNamesString && (
            <Grid>
              <Alert
                severity={isPast ? "warning" : "info"}
                icon={isPast ? <AlarmOff /> : <AlarmOn />}
              >
                {isPast ? (
                  <Typography>
                    {t(`reminder_date_text_past_due_${label}`, {
                      displayNames: displayNamesString?.replace(
                        t("You")!,
                        t("you")
                      ),
                      date: new Date(reminderDate),
                      format: "dateOnly",
                    })}
                  </Typography>
                ) : (
                  <Typography>
                    {t(`reminder_date_text_${label}`, {
                      displayNames: displayNamesString,
                      date: new Date(reminderDate),
                      format: "dateOnly",
                    })}
                  </Typography>
                )}
              </Alert>
            </Grid>
          )}
        </Grid>
      </ListItemText>
    </ListItem>
  );
};

export default ContractExpiryNoteAsListItem;
